import type { AppLocale } from '@/shared/i18n'
import {
  calendarWindowEnd,
  weekStartsOnFor,
  type WeekStartsOn,
} from '../lib/raidCalendar'
import { RaidStageKind as RaidStageKindValue } from '../types'
import type { RaidStage, RaidStageKind, RaidSummary } from '../types'

export interface RaidSummarySnapshot {
  readonly id: number
  readonly guildId: number
  readonly name: string
  readonly isPublished: boolean
  readonly scheduledStartsAt: string | null
  readonly durationMinutes: number | null
  readonly cancelledAt: string | null
  readonly completedAt: string | null
  readonly sourceRaidId: number | null
  readonly recurrence: {
    readonly seriesId: number
    readonly parentId: number
    readonly index: number
    readonly total: number
    readonly frequency: string
  } | null
  readonly notes: string
  readonly createdBy: number | null
  readonly createdAt: string
  readonly updatedAt: string
}

export interface RaidStageSnapshot {
  readonly id: number
  readonly raidId: number
  readonly kind: string
  readonly name: string
  readonly notes: string
  readonly orderIndex: number
}

function mapRaidSummary(snapshot: RaidSummarySnapshot): RaidSummary {
  return {
    ...snapshot,
    id: String(snapshot.id),
    guildId: String(snapshot.guildId),
    sourceRaidId: snapshot.sourceRaidId === null ? null : String(snapshot.sourceRaidId),
    recurrence: snapshot.recurrence === null
      ? null
      : {
          ...snapshot.recurrence,
          seriesId: String(snapshot.recurrence.seriesId),
          parentId: String(snapshot.recurrence.parentId),
        },
    createdBy: snapshot.createdBy === null ? null : String(snapshot.createdBy),
  }
}

export function mapAndSortRaidSummaries(
  snapshots: ReadonlyArray<RaidSummarySnapshot>,
): ReadonlyArray<RaidSummary> {
  return snapshots.map(mapRaidSummary).sort((left, right) => {
    const leftKey = left.scheduledStartsAt ?? ''
    const rightKey = right.scheduledStartsAt ?? ''
    if (leftKey !== rightKey) return leftKey < rightKey ? -1 : 1
    return left.name.localeCompare(right.name)
  })
}

export function raidsInPlannerWindow(
  raids: ReadonlyArray<RaidSummary>,
  start: Date,
  locale: AppLocale,
): ReadonlyArray<RaidSummary> {
  const weekStartsOn: WeekStartsOn = weekStartsOnFor(locale)
  const from = start.getTime()
  const to = calendarWindowEnd(start, weekStartsOn).getTime()
  return raids.filter((raid) => {
    if (raid.scheduledStartsAt === null) return false
    const timestamp = new Date(raid.scheduledStartsAt).getTime()
    return Number.isFinite(timestamp) && timestamp >= from && timestamp < to
  })
}

function parseRaidStageKind(kind: string): RaidStageKind {
  switch (kind) {
    case RaidStageKindValue.Opening:
      return RaidStageKindValue.Opening
    case RaidStageKindValue.Forest:
      return RaidStageKindValue.Forest
    case RaidStageKindValue.MidPush:
      return RaidStageKindValue.MidPush
    case RaidStageKindValue.LateDefense:
      return RaidStageKindValue.LateDefense
    case RaidStageKindValue.Endgame:
      return RaidStageKindValue.Endgame
    default:
      return RaidStageKindValue.Custom
  }
}

export function mapRaidStages(
  stages: ReadonlyArray<RaidStageSnapshot>,
): ReadonlyArray<RaidStage> {
  return [...stages]
    .sort((left, right) => left.orderIndex - right.orderIndex)
    .map((stage) => ({
      id: String(stage.id),
      raidId: String(stage.raidId),
      kind: parseRaidStageKind(stage.kind),
      name: stage.name,
      notes: stage.notes,
      orderIndex: stage.orderIndex,
    }))
}
